/**
 * Store Pinia — oidc (wrapper léger autour de oidcService).
 *
 * Migré depuis `store/modules/oidc.js`. Garde la même interface (getters, actions)
 * que l'ancien module Vuex pour minimiser les changements dans les composants.
 *
 * Ce fichier contient AUSSI les effets de bord d'initialisation OIDC (enregistrement
 * des callbacks oidcService + oidcService.init()), qui étaient déclenchés par l'import
 * du module dans store/index.js. Ils sont désormais déclenchés par l'import de ce
 * fichier dans main.js.
 *
 * Note : les getters lisent directement oidcService (source de vérité). Le state
 * (user/tokens) est conservé pour compat mais n'est pas la source des getters →
 * non persisté (oidc-client-ts gère sa propre persistance des tokens).
 */
import { defineStore } from 'pinia'
import oidcService from '@/auth/oidcService'
import pinia from '@/stores'
import { useDialogStore } from '@/stores/dialog'
import { useUserInfoStore } from '@/stores/userInfo'
import { useParametersStore } from '@/stores/parameters'
import { MessagesService, ParametersService } from '@/services'
import logger from '@/utils/logger'

// ── Rôles OIDC (configurés via variables d'environnement) ────────────
const ROLE_NIVEAU0 = process.env.VUE_APP_ROLE_NIVEAU0
  ? process.env.VUE_APP_ROLE_NIVEAU0.split(',').map(s => s.trim().toUpperCase())
  : []
const ROLE_ADMIN = process.env.VUE_APP_ROLE_ADMIN
  ? process.env.VUE_APP_ROLE_ADMIN.split(',').map(s => s.trim().toUpperCase())
  : []
const SUPER_ADMINS_OIDC = process.env.VUE_APP_SUPER_ADMIN
  ? process.env.VUE_APP_SUPER_ADMIN.split(',').map(s => s.trim().toLowerCase())
  : []
const POST_LOGIN_DIALOG_KEY = 'postLoginDialogShown_v2_20260316'

function buildRoles (sub, email) {
  const roles = []
  const upperSub = sub.toUpperCase()
  if (ROLE_ADMIN.some(x => x === upperSub)) roles.push('admin')
  if (ROLE_NIVEAU0.some(x => x === upperSub)) roles.push('niveau0')
  if (email && SUPER_ADMINS_OIDC.includes(email.toLowerCase())) roles.push('superAdmin')
  roles.push('user')
  return roles
}

/**
 * Affiche un dialog d'information une seule fois après la connexion OIDC (UQAM).
 * Même logique que Guard.js (MSAL). Voir la clé versionnée POST_LOGIN_DIALOG_KEY.
 */
async function showPostLoginDialogOnce () {
  const notificationEnabled = useParametersStore(pinia).getNotificationMessageConnection
  if (notificationEnabled !== '1') return
  if (sessionStorage.getItem(POST_LOGIN_DIALOG_KEY)) return
  sessionStorage.setItem(POST_LOGIN_DIALOG_KEY, '1')

  const messageData = await MessagesService.search(41)
  if (!messageData) return

  const langue = localStorage.getItem('APP_LANG_KEY') || 'fr'
  const message = messageData[langue] || messageData.fr || ''
  if (!message) return

  useDialogStore(pinia).showDialog({
    message,
    print: false,
    html: true,
    iconState: true,
    iconColor: 'success',
    iconName: 'mdi-check-circle',
  })
}

// ── Promesse résolue quand onUserLoaded a fini son travail async ─────
// Permet à oidcSignInCallback d'attendre que le store soit peuplé (rôles,
// paramètres) avant de retourner le redirectPath (évite la race condition).
let _userLoadedResolve = null
let _userLoadedPromise = null

function resetUserLoadedPromise () {
  _userLoadedPromise = new Promise((resolve) => {
    _userLoadedResolve = resolve
  })
}
resetUserLoadedPromise()

// ── Store Pinia ──────────────────────────────────────────────────────
export const useOidcStore = defineStore('oidc', {
  state: () => ({
    user: oidcService.user,
    access_token: oidcService.accessToken,
    id_token: oidcService.idToken,
    is_checked: oidcService.authenticationIsChecked,
  }),

  getters: {
    oidcIsAuthenticated: () => oidcService.isAuthenticated,
    oidcAuthenticationIsChecked: () => oidcService.authenticationIsChecked,
    oidcUser: () => oidcService.profile,
    oidcIdToken: () => oidcService.idToken,
    oidcIdTokenExp: () => oidcService.idTokenExp,
    oidcAccessToken: () => oidcService.accessToken,
  },

  actions: {
    // ─── ex-mutations ───────────────────────────────────────────────
    setUser (user) {
      this.user = user
      this.access_token = user ? user.access_token : null
      this.id_token = user ? user.id_token : null
      this.is_checked = true
    },
    unsetOidcAuth () {
      this.user = null
      this.access_token = null
      this.id_token = null
    },

    // ─── actions ────────────────────────────────────────────────────
    async authenticateOidc () {
      return oidcService.signIn()
    },
    async authenticateOidcSilent () {
      try {
        return await oidcService.signInSilent()
      } catch (error) {
        logger.error('OIDC silent auth error:', error)
      }
    },
    async oidcSignInCallback () {
      // Réinitialiser la promesse avant le callback pour capter le prochain onUserLoaded
      resetUserLoadedPromise()
      const redirectPath = await oidcService.signInCallback()
      // Attendre que onUserLoaded ait fini de peupler le store (rôles, paramètres, etc.)
      await _userLoadedPromise
      return redirectPath
    },
    async signOutOidc () {
      return oidcService.signOut()
    },
    async getOidcUser () {
      return oidcService.user
    },
  },
})

// ── Effets de bord OIDC (enregistrement des callbacks + init) ────────
// Exécutés à l'import de ce fichier (depuis main.js), comme l'ancien module Vuex
// l'était via store/index.js.

oidcService.onUserLoaded(async (user) => {
  // Charger les paramètres avant d'afficher le dialog (même logique que Guard.js)
  const parametersStore = useParametersStore(pinia)
  if (!parametersStore.parameters.length) {
    try {
      const parameters = await ParametersService.listAll()
      parametersStore.setParameters(parameters)
    } catch (error) {
      logger.error('Erreur chargement paramètres OIDC:', error)
    }
  }

  await showPostLoginDialogOnce()

  useUserInfoStore(pinia).updateUser({
    name: user.profile.family_name,
    firstName: user.profile.given_name,
    mail: user.profile.email,
    displayName: `${user.profile.family_name}, ${user.profile.given_name}`,
    username: user.profile.email,
    roles: buildRoles(user.profile.sub, user.profile.email),
    codeMs: user.profile.sub,
  })

  useOidcStore(pinia).setUser(user)

  // Signaler que le store est prêt
  if (_userLoadedResolve) _userLoadedResolve()
})

oidcService.onUserUnloaded(() => {
  sessionStorage.removeItem(POST_LOGIN_DIALOG_KEY)
  useUserInfoStore(pinia).resetUser()
  // On vide les paramètres pour forcer le rechargement à la prochaine connexion
  useParametersStore(pinia).setParameters([])
  useOidcStore(pinia).unsetOidcAuth()
})

let authenticateSilentListener = null

oidcService.onAccessTokenExpiring(() => {
  authenticateSilentListener = () => {
    useOidcStore(pinia).authenticateOidcSilent()
    document.removeEventListener('mousemove', authenticateSilentListener)
  }
  document.addEventListener('mousemove', authenticateSilentListener)
})

oidcService.onAccessTokenExpired(() => {
  if (authenticateSilentListener) {
    document.removeEventListener('mousemove', authenticateSilentListener)
  }
  useOidcStore(pinia).signOutOidc()
})

// Initialiser le service au chargement
oidcService.init()
